import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Shield as ShieldIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import CTAButton from '../components/common/CTAButton';

const AboutPage: React.FC = () => {
  const values = [
    {
      icon: ShieldIcon,
      title: 'Compliance First',
      description: 'Every feature we build starts with compliance in mind. We believe that ethical collection practices lead to better outcomes for everyone.',
    },
    {
      icon: TrendingUpIcon,
      title: 'Continuous Innovation',
      description: 'The collection industry is evolving, and so are we. We constantly improve our platform based on user feedback and industry changes.',
    },
    {
      icon: PeopleIcon,
      title: 'Customer Success',
      description: "Your success is our success. We're not just a software vendor - we're your partner in building a more efficient collection operation.",
    },
    {
      icon: SupportIcon,
      title: 'Responsive Support',
      description: 'Get help from people who understand your business. Support runs during business hours today, with extended coverage planned at launch.',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          py: 12,
          background: 'radial-gradient(ellipse at 50% 0%, rgba(100, 181, 246, 0.15) 0%, transparent 70%)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, mb: 3 }}>
              Built by Collection
              <Box
                component="span"
                sx={{
                  display: 'block',
                  background: 'linear-gradient(135deg, #64b5f6 0%, #f48fb1 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Professionals
              </Box>
            </Typography>
            <Typography variant="h5" color="text.secondary">
              We understand the challenges of debt collection because we've lived them.
              Debtornator was born from decades of industry experience and a vision for
              a better way to collect.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Story Section */}
      <Box sx={{ py: 12, bgcolor: 'background.paper' }}>
        <Container maxWidth="md">
          <Typography variant="h2" textAlign="center" gutterBottom>
            Our Story
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Debtornator started in 2025 when our founders, frustrated with outdated
            collection software and compliance nightmares, decided to build something better.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            After years of working with clunky systems that seemed designed to create compliance
            problems rather than prevent them, we knew there had to be a better way. We assembled
            a team of collection industry veterans and world-class engineers to build the platform
            we always wished we had.
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Debtornator launches in Q3 2027, built from the ground up to help agencies
            collect more while staying compliant. Our mission is to transform the collection
            industry through technology that works for everyone - collectors, creditors, and
            consumers alike. This is just the beginning.
          </Typography>
        </Container>
      </Box>

      {/* Values Section */}
      <Box sx={{ py: 12 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" textAlign="center" gutterBottom>
            Our Values
          </Typography>
          <Typography variant="h6" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
            The principles that guide everything we do
          </Typography>
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #64b5f6 0%, #f48fb1 100%)',
                          flexShrink: 0,
                        }}
                      >
                        <value.icon sx={{ fontSize: 28, color: 'white' }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" gutterBottom fontWeight={600}>
                          {value.title}
                        </Typography>
                        <Typography color="text.secondary">
                          {value.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 12, bgcolor: 'background.paper' }}>
        <Container maxWidth="md">
          <Card
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(100, 181, 246, 0.1) 0%, rgba(244, 143, 177, 0.1) 100%)',
              border: '1px solid rgba(100, 181, 246, 0.3)',
            }}
          >
            <Typography variant="h3" gutterBottom>
              See It In Action
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              The best way to understand Debtornator is to use it. Explore a live,
              fully-seeded demo — including the compliance engine in action.
            </Typography>
            <CTAButton to="/request-demo">
              Try the Live Demo
            </CTAButton>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutPage;
